import { ReportRequest, SimulationCampaignsReport } from '@/client/reports';
import { Actions, Subjects } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import {
  ChartEmptyState,
  ChartEmptyStateType,
} from '@/components/dashboards/admin';
import { DataTable, DataTableColumnType } from '@/components/tables/crud';
import { useSimulationCampaignsReports } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermission } from '@/hooks/usePermission';
import { selectCurrentAccount } from '@/store/features/account';
import { DateFormats } from '@/system-settings/enums';
import { FormatDate } from '@/ui/date';
import { FlexContainer } from '@/ui/styled-ui';
import { ChevronDownIcon } from '@/utils/helpers';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { default as React, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { default as styled } from 'styled-components';

const StyledDiv = styled.div<{ zoomLevel: number }>`
  width: 100%;
  padding-top: var(--default-padding);
  position: relative;

  .p-datatable {
    border: 1px solid var(--gray-medium);
    border-radius: var(--xsmall-border-radius);

    th[role='columnheader'] {
      color: var(--black-main) !important;
      background-color: var(--white-main) !important;
    }

    tr {
      &:nth-child(even) {
        background-color: var(--white-main) !important;

        &:hover {
          background-color: var(--white-main) !important;
        }
      }

      &:nth-child(odd) {
        background-color: var(--beige-main) !important;

        &:hover {
          background-color: var(--beige-main) !important;
        }
      }
    }

    td[role='cell'] {
      padding: var(--small-padding) !important;
    }

    span.p-column-title {
      min-width: max-content;
    }

    th.date-column {
      min-width: ${({ zoomLevel }) => (zoomLevel > 1.25 ? '100px' : '130px')};
    }
    .p-datatable-thead tr th {
      &:first-child {
        border-top-left-radius: var(--xsmall-border-radius);
      }
      &:last-child {
        border-top-right-radius: var(--xsmall-border-radius);
      }
    }
  }

  &.limited-view {
    &::after {
      content: '';
      position: absolute;
      bottom: 0px;
      left: 0;
      right: 0;
      height: 160px;
      background: linear-gradient(
        to bottom,
        transparent 0%,
        var(--beige-main) 50%,
        var(--beige-main) 100%
      );
      pointer-events: none;
      z-index: 1;
    }
  }

  @media (max-width: 1367px) {
    .p-datatable {
      th.date-column {
        min-width: 100px;
      }
    }
  }
`;

const ViewAllButton = styled(FlexContainer)<{ showAll: boolean }>`
  padding-bottom: var(--medium-padding);
  padding-top: ${({ showAll }) => (showAll ? 'var(--default-padding)' : '0px')};
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  font-size: var(--small-font-size);
  font-weight: 400;
  cursor: pointer;
  transition: color 0.2s ease;
  position: relative;
  z-index: 2;
`;

const StyledRate = styled(FlexContainer)`
  min-width: max-content;
  .red,
  .green {
    font-weight: 500;
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
  }
  .red {
    color: var(--red-main);
  }
  .green {
    color: var(--green-main);
  }
  .gray {
    color: var(--gray-darker);
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
  }
`;

const INITIAL_ROWS = 6;

type SimulationListProps = {
  filters: ReportRequest;
};

export const SimulationList = ({ filters }: SimulationListProps) => {
  const account = useAppSelector(selectCurrentAccount);
  const { t } = useTranslation();
  const { canUseFeature } = useFeatureFlag();
  const { can } = usePermission();
  const hasReportingEnabled = canUseFeature(
    Subjects.OUTLOOK_PHISHING_EMAIL_REPORTING,
  );
  const navigate = useNavigate();
  const { sort, onSort } = useTable();
  const zoomLevel = window.devicePixelRatio || 1;

  const [showAll, setShowAll] = useState(false);

  const { isLoading, simulationCampaigns, isFetching } =
    useSimulationCampaignsReports(account?.id as string, {
      ...filters,
      sort: sort && sort?.length > 0 ? [sort.join(',')] : [],
    });

  const handleViewAllClick = () => {
    setShowAll(!showAll);
  };

  const hasMoreData =
    simulationCampaigns && simulationCampaigns.length > INITIAL_ROWS;
  const showViewAllButton = hasMoreData && !showAll;

  const allColumns: DataTableColumnType[] = [
    {
      field: 'name',
      header: t('phishing.simulation'),
      sortable: false,
      filterable: false,
      style: {
        maxWidth: '300px',
      },
      render: (row: SimulationCampaignsReport) => (
        <FlexContainer direction="column" align="flex-start">
          <span className="mr-2">{row?.name}</span>
          {row?.phishingSign && (
            <span className="secondary-text">
              {t('phishing.simulation.sign', {
                sign: t(`campaign.template.${row.phishingSign}`),
              })}
            </span>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'launchDate',
      header: t('generic.startDate'),
      sortable: true,
      filterable: false,
      headerClassName: 'date-column',
      render: (row: SimulationCampaignsReport) =>
        row?.launchDate ? (
          <FormatDate date={row?.launchDate} format={DateFormats.DATEONLY} />
        ) : (
          '—'
        ),
    },
    {
      field: 'finishDate',
      header: t('generic.endDate'),
      sortable: true,
      filterable: false,
      headerClassName: 'date-column',
      render: (row: SimulationCampaignsReport) =>
        row?.finishDate ? (
          <FormatDate date={row?.finishDate} format={DateFormats.DATEONLY} />
        ) : (
          '—'
        ),
    },
    {
      field: 'sentCount',
      header: t('campaign.emailSent'),
      sortable: true,
      filterable: false,
      render: (row: SimulationCampaignsReport) => row?.sentCount || 0,
    },
    {
      field: 'clickedRate',
      header: t('phishing.chart.clickRate'),
      sortable: true,
      filterable: false,
      render: (row: SimulationCampaignsReport) => (
        <StyledRate direction="column" align="flex-start">
          <span
            className={
              Number(row.clickedRate) >= Number(row.clickedRateBenchmark) &&
              Number(row.clickedRate) !== 0
                ? 'red'
                : 'green'
            }
          >
            {parseFloat(Number(row.clickedRate).toFixed(2))}%
          </span>
          <span className="gray">
            {t('phishing.simulation.benchmark', {
              benchmark:
                parseFloat(Number(row.clickedRateBenchmark).toFixed(2)) || 0,
            })}
          </span>
        </StyledRate>
      ),
    },
    {
      field: 'submittedRate',
      header: t('phishing.chart.submissionRate'),
      sortable: true,
      filterable: false,
      render: (row: SimulationCampaignsReport) => (
        <StyledRate direction="column" align="flex-start">
          <span
            className={
              Number(row.submittedRate) >= Number(row.submittedRateBenchmark) &&
              Number(row.submittedRate) !== 0
                ? 'red'
                : 'green'
            }
          >
            {parseFloat(Number(row.submittedRate).toFixed(2))}%
          </span>
          <span className="gray">
            {t('phishing.simulation.benchmark', {
              benchmark:
                parseFloat(Number(row.submittedRateBenchmark).toFixed(2)) || 0,
            })}
          </span>
        </StyledRate>
      ),
    },
    {
      field: 'hasFalsePositives',
      header: t('campaign.falsePositives'),
      sortable: false,
      filterable: false,
      style: {
        width: '80px',
        textAlign: 'center' as const,
      },
      render: (row: SimulationCampaignsReport) =>
        row?.hasFalsePositives
          ? t('campaign.falsePositives.yes')
          : t('campaign.falsePositives.no'),
    },
    {
      field: 'reportedRate',
      header: t('phishing.chart.reportRate'),
      sortable: true,
      filterable: false,
      render: (row: SimulationCampaignsReport) =>
        parseFloat(Number(row.reportedRate).toFixed(2)) + '%',
    },
  ];

  const columns = allColumns
    .filter((column) => hasReportingEnabled || column.field !== 'reportedRate')
    .filter(
      (column) =>
        can(Actions.READ, Subjects.FALSE_POSITIVE_IPS) ||
        column.field !== 'hasFalsePositives',
    );

  const onRowClick = (event: DataTableRowClickEvent) => {
    navigate(
      RedirectPaths[RedirectPathsEnum.CAMPAIGNS_REPORT](event.data.campaignId),
    );
  };

  if (!isLoading && !simulationCampaigns?.length) {
    return (
      <ChartEmptyState
        minHeight={510}
        type={ChartEmptyStateType.PHISHING}
        title={t('phishing.simulations.empty.title')}
        description={t('phishing.simulations.empty.description')}
      />
    );
  }

  return (
    <StyledDiv
      className={showViewAllButton ? 'limited-view' : ''}
      zoomLevel={zoomLevel}
    >
      <DataTable
        id="simulation-list"
        data={(showAll
          ? simulationCampaigns
          : simulationCampaigns?.slice(0, INITIAL_ROWS)
        )?.map((x) => ({
          ...x,
          id: x.campaignId,
        }))}
        count={
          showAll
            ? (simulationCampaigns?.length as number)
            : Math.min(simulationCampaigns?.length || 0, INITIAL_ROWS)
        }
        isLoading={isLoading}
        isFetching={isFetching}
        columns={columns}
        rows={showAll ? undefined : INITIAL_ROWS}
        onSort={onSort}
        sort={sort}
        onRowClick={onRowClick}
      />

      {showViewAllButton && (
        <ViewAllButton onClick={handleViewAllClick} showAll={showAll}>
          <span>{t('phishing.simulation.viewAll')}</span>
          <ChevronDownIcon />
        </ViewAllButton>
      )}

      {showAll && hasMoreData && (
        <ViewAllButton
          id="extend-table-button"
          onClick={handleViewAllClick}
          showAll={showAll}
        >
          <span>{t('phishing.simulation.viewLess')}</span>
          <ChevronDownIcon rotated />
        </ViewAllButton>
      )}
    </StyledDiv>
  );
};
